import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as PackIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { TestMaterials } from '../components/TestMaterials';
import { InputChips, KindChip, MaterialPreview } from '../components/tests/common';
import {
  CaseEditorDialog,
  MATERIAL_KINDS,
  TagInput,
  TargetPicker,
  UploadDialog,
  errorText,
} from '../components/tests/dialogs';
import { Material, MaterialPage, TargetOption, TestCase, TestsApi, useTestsAccess } from '../services/testsApi';

const PAGE_SIZE = 48;

const targetUrl = (type: string, id: string, functionId?: string, customerId?: string | null) => {
  const q = customerId ? `&customer_id=${encodeURIComponent(customerId)}` : '';
  return type === 'chain' ? `/dashboard/chain-performance/${id}?tab=tests${q}` : `/dashboard/function-runs/${functionId || id}?tab=tests${q}`;
};

export default function TestLibrary() {
  const { getIdToken } = useAuth();
  const [params, setParams] = useSearchParams();
  const [account, setAccount] = useState<string | null>(params.get('customer_id'));
  const { api, access, enabled, loading } = useTestsAccess(getIdToken, account);
  const tab = params.get('tab') === 'cases' ? 'cases' : 'materials';
  const [notice, setNotice] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [installing, setInstalling] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  if (loading) return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>;
  if (!enabled) return <TestMaterials />;

  const setTab = (t: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', t);
    setParams(next, { replace: true });
  };

  const platformId = access?.platform_account_id;
  const switchAccount = (id: string | null) => {
    setAccount(id);
    const next = new URLSearchParams(params);
    if (id) next.set('customer_id', id);
    else next.delete('customer_id');
    setParams(next, { replace: true });
  };

  const installPack = async () => {
    setInstalling(true);
    try {
      const r = await api.installPack();
      setNotice({
        severity: 'success',
        text: `Starter pack: ${r.materials} materials and ${r.cases} cases added, ${r.links} enabled on platform routes` +
          (r.already_installed ? ` (${r.already_installed} already installed)` : '') +
          (r.skipped_links.length ? `. Skipped: ${r.skipped_links.join(', ')}` : '.'),
      });
      setReloadKey(k => k + 1);
    } catch (e) {
      setNotice({ severity: 'error', text: errorText(e) });
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h4">Test Library</Typography>
          <Typography color="text.secondary">
            Files and inputs to test with, and the test cases built from them. Enable cases on a function or chain to compare models
            on its Tests tab.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {access?.is_admin && platformId && (
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Library</InputLabel>
              <Select label="Library" value={account || ''} onChange={e => switchAccount(e.target.value || null)}>
                <MenuItem value="">My account</MenuItem>
                <MenuItem value={platformId}>Platform account</MenuItem>
              </Select>
            </FormControl>
          )}
          {access?.is_admin && account && account === platformId && (
            <Tooltip title="Adds the platform starter pack (photos, labels, menus, audio, video) and enables its cases on the platform routes. Safe to run again.">
              <span>
                <Button variant="outlined" startIcon={<PackIcon />} onClick={installPack} disabled={installing}>
                  {installing ? 'Installing…' : 'Install starter pack'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2 }}>
          {notice.text}
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="materials" label="Materials" />
        <Tab value="cases" label="Test cases" />
      </Tabs>
      {tab === 'materials' ? (
        <MaterialsTab key={`m-${account}-${reloadKey}`} api={api} maxSize={access?.limits.max_file_size || ''} onNotice={setNotice} onCases={() => setTab('cases')} />
      ) : (
        <CasesTab key={`c-${account}-${reloadKey}`} api={api} account={account} onNotice={setNotice} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

type Notify = (n: { severity: 'success' | 'error' | 'info'; text: string }) => void;

const MaterialsTab: React.FC<{ api: TestsApi; maxSize: string; onNotice: Notify; onCases: () => void }> = ({ api, maxSize, onNotice, onCases }) => {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<MaterialPage | null>(null);
  const [selected, setSelected] = useState<Record<string, Material>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [textOpen, setTextOpen] = useState(false);
  const [editing, setEditing] = useState<Material | null>(null);
  const [casesOpen, setCasesOpen] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    api
      .materials({ search: query || undefined, kind: kind || undefined, tag: tag || undefined, page, page_size: PAGE_SIZE })
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && onNotice({ severity: 'error', text: errorText(e) }));
    return () => {
      cancelled = true;
    };
  }, [api, query, kind, tag, page, reload, onNotice]);

  const refresh = () => setReload(r => r + 1);
  const picked = Object.values(selected);
  const toggle = (m: Material) =>
    setSelected(s => {
      const next = { ...s };
      if (next[m.id]) delete next[m.id];
      else next[m.id] = m;
      return next;
    });

  const remove = async (m: Material) => {
    const warn = m.used_by ? ` It is used by ${m.used_by} test case${m.used_by === 1 ? '' : 's'}, which will stop working.` : '';
    if (!window.confirm(`Delete "${m.name}"?${warn}`)) return;
    try {
      await api.deleteMaterial(m.id);
      setSelected(s => {
        const next = { ...s };
        delete next[m.id];
        return next;
      });
      refresh();
    } catch (e) {
      onNotice({ severity: 'error', text: errorText(e) });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search names and descriptions"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Kind</InputLabel>
          <Select label="Kind" value={kind} onChange={e => { setKind(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {MATERIAL_KINDS.map(k => <MenuItem key={k} value={k} sx={{ textTransform: 'capitalize' }}>{k}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tag</InputLabel>
          <Select label="Tag" value={tag} onChange={e => { setTag(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {(data?.tags || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setTextOpen(true)}>
          Text or JSON
        </Button>
        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>
          Upload
        </Button>
      </Stack>

      {picked.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2">{picked.length} selected</Typography>
          <Button size="small" variant="contained" onClick={() => setCasesOpen(true)}>
            New test case{picked.length === 1 ? '' : 's'} from selected
          </Button>
          <Button size="small" onClick={() => setSelected({})}>
            Clear
          </Button>
        </Paper>
      )}

      {!data ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !data.items.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography gutterBottom>{query || kind || tag ? 'Nothing matches.' : 'The library is empty.'}</Typography>
          {!query && !kind && !tag && (
            <Typography variant="body2" color="text.secondary">
              Upload photos, audio, video, PDFs or text your functions should handle. Then turn them into test cases.
            </Typography>
          )}
        </Paper>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            {data.total} item{data.total === 1 ? '' : 's'} · {data.count} of {data.limit} used
          </Typography>
          <Grid container spacing={1.5}>
            {data.items.map(m => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={m.id}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderColor: selected[m.id] ? 'primary.main' : undefined }}>
                  <Box sx={{ position: 'relative' }}>
                    <MaterialPreview api={api} material={m} />
                    <Checkbox
                      size="small"
                      checked={!!selected[m.id]}
                      onChange={() => toggle(m)}
                      sx={{ position: 'absolute', top: 2, left: 2, bgcolor: 'background.paper', borderRadius: 1, p: 0.25 }}
                    />
                  </Box>
                  <Box sx={{ p: 1.25, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={m.name}>
                      {m.name}
                    </Typography>
                    {m.description && (
                      <Typography variant="caption" color="text.secondary" component="div" noWrap title={m.description}>
                        {m.description}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                      <KindChip kind={m.kind} />
                      {m.tags.slice(0, 3).map(t => <Chip key={t} size="small" label={t} onClick={() => { setTag(t); setPage(1); }} />)}
                      {m.tags.length > 3 && <Chip size="small" label={`+${m.tags.length - 3}`} />}
                    </Stack>
                    {m.attribution?.author && (
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }} noWrap>
                        {m.attribution.author}
                        {m.attribution.license ? ` · ${m.attribution.license}` : ''}
                      </Typography>
                    )}
                  </Box>
                  <CardActions sx={{ pt: 0, justifyContent: 'space-between' }}>
                    <Tooltip title="Test cases that use this">
                      <Typography variant="caption" color="text.secondary" sx={{ cursor: m.used_by ? 'pointer' : 'default' }} onClick={m.used_by ? onCases : undefined}>
                        {m.used_by ? `Used by ${m.used_by}` : 'Not used yet'}
                      </Typography>
                    </Tooltip>
                    <Box>
                      <IconButton size="small" onClick={() => setEditing(m)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(m)} aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          {data.total > PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={Math.ceil(data.total / PAGE_SIZE)} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      <UploadDialog
        open={uploadOpen}
        api={api}
        tagOptions={data?.tags || []}
        maxSize={maxSize}
        onClose={() => setUploadOpen(false)}
        onDone={r => {
          setUploadOpen(false);
          const parts = [`${r.created.length} added`];
          if (r.already_in_library.length) parts.push(`${r.already_in_library.length} already in the library`);
          if (r.failed.length) parts.push(`${r.failed.length} failed: ${r.failed.map(f => `${f.file} (${f.error})`).join(', ')}`);
          onNotice({ severity: r.failed.length ? 'error' : 'success', text: `${parts.join(', ')}.` });
          refresh();
        }}
      />
      <TextMaterialDialog open={textOpen} api={api} tagOptions={data?.tags || []} onClose={() => setTextOpen(false)} onSaved={() => { setTextOpen(false); refresh(); }} />
      <EditMaterialDialog material={editing} api={api} tagOptions={data?.tags || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />
      <CasesFromMaterialsDialog
        open={casesOpen}
        api={api}
        materials={picked}
        onClose={() => setCasesOpen(false)}
        onDone={text => {
          setCasesOpen(false);
          setSelected({});
          onNotice({ severity: 'success', text });
          refresh();
        }}
      />
    </Box>
  );
};

const TextMaterialDialog: React.FC<{ open: boolean; api: TestsApi; tagOptions: string[]; onClose: () => void; onSaved: () => void }> = ({
  open,
  api,
  tagOptions,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [isJson, setIsJson] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName('');
      setText('');
      setIsJson(false);
      setTags([]);
      setError(null);
    }
  }, [open]);
  const save = async () => {
    let json: unknown;
    if (isJson) {
      try {
        json = JSON.parse(text);
      } catch {
        return setError("That isn't valid JSON.");
      }
    }
    setSaving(true);
    try {
      await api.createTextMaterial({ name: name.trim(), tags, ...(isJson ? { json } : { text }) });
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New text or JSON input</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth autoFocus />
          <Stack direction="row" spacing={1}>
            <Chip label="Text" color={!isJson ? 'primary' : 'default'} onClick={() => setIsJson(false)} />
            <Chip label="JSON" color={isJson ? 'primary' : 'default'} onClick={() => setIsJson(true)} />
          </Stack>
          <TextField
            value={text}
            onChange={e => setText(e.target.value)}
            multiline
            minRows={6}
            fullWidth
            InputProps={{ sx: { fontFamily: isJson ? 'monospace' : undefined } }}
            placeholder={isJson ? '{"items": ["apple", "banana"]}' : 'e.g. 2 eggs scrambled with toast and a coffee'}
          />
          <TagInput value={tags} onChange={setTags} options={tagOptions} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !name.trim() || !text.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditMaterialDialog: React.FC<{ material: Material | null; api: TestsApi; tagOptions: string[]; onClose: () => void; onSaved: () => void }> = ({
  material,
  api,
  tagOptions,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (material) {
      setName(material.name);
      setDescription(material.description || '');
      setTags(material.tags);
      setError(null);
    }
  }, [material]);
  const save = async () => {
    if (!material) return;
    try {
      await api.updateMaterial(material.id, { name: name.trim(), description, tags });
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Dialog open={!!material} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {material?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" fullWidth />
          <TextField label="Description" value={description} onChange={e => setDescription(e.target.value)} size="small" fullWidth multiline />
          <TagInput value={tags} onChange={setTags} options={tagOptions} />
          {material?.attribution && (
            <Typography variant="caption" color="text.secondary">
              Source: {material.attribution.title || material.name}
              {material.attribution.author ? ` by ${material.attribution.author}` : ''}
              {material.attribution.license ? ` (${material.attribution.license})` : ''}
              {material.attribution.url ? ` · ${material.attribution.url}` : ''}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** One case per selected material, with an optional shared note, optionally enabled on a target. */
const CasesFromMaterialsDialog: React.FC<{
  open: boolean;
  api: TestsApi;
  materials: Material[];
  onClose: () => void;
  onDone: (text: string) => void;
}> = ({ open, api, materials, onClose, onDone }) => {
  const [message, setMessage] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [target, setTarget] = useState<TargetOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setMessage('');
      setTags(Array.from(new Set(materials.flatMap(m => m.tags))).slice(0, 5));
      setTarget(null);
      setError(null);
    }
  }, [open, materials]);
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const ids: string[] = [];
      for (const m of materials) {
        const inputs = [{ field: 'input', material_id: m.id }, ...(message.trim() ? [{ field: 'message', text: message.trim() }] : [])];
        const c = await api.createCase({ name: m.name, tags, inputs });
        ids.push(c.id);
      }
      if (target) await api.linkCases(ids, target.target_type, target.target_id);
      onDone(`${ids.length} test case${ids.length === 1 ? '' : 's'} created${target ? ` and enabled on ${target.name}. Add what each should return on its Tests tab.` : '.'}`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        New test case{materials.length === 1 ? '' : 's'} from {materials.length} item{materials.length === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Each item becomes its own test case, named after it.
          </Typography>
          <TextField
            label="Note sent with each input (optional)"
            value={message}
            onChange={e => setMessage(e.target.value)}
            size="small"
            fullWidth
            multiline
          />
          <TagInput value={tags} onChange={setTags} />
          <TargetPicker api={api} value={target} onChange={setTarget} label="Enable on (optional)" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={create} disabled={busy || !materials.length}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Test cases
// ---------------------------------------------------------------------------

const CasesTab: React.FC<{ api: TestsApi; account: string | null; onNotice: Notify }> = ({ api, account, onNotice }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [tag, setTag] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ items: TestCase[]; total: number; tags: string[] } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<TestCase | null | undefined>(undefined);
  const [enableOpen, setEnableOpen] = useState(false);
  const [target, setTarget] = useState<TargetOption | null>(null);
  const [reload, setReload] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => setQuery(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    api
      .cases({ search: query || undefined, tag: tag || undefined, page, page_size: pageSize })
      .then(d => !cancelled && setData(d))
      .catch(e => !cancelled && onNotice({ severity: 'error', text: errorText(e) }));
    return () => {
      cancelled = true;
    };
  }, [api, query, tag, page, reload, onNotice]);

  const refresh = useCallback(() => setReload(r => r + 1), []);
  const toggle = (id: string) =>
    setSelected(s => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const items = data?.items || [];
  const allOn = items.length > 0 && items.every(c => selected.has(c.id));

  const remove = async (c: TestCase) => {
    const used = c.links.filter(l => l.enabled).length;
    if (!window.confirm(`Delete "${c.name}"?${used ? ` It is enabled on ${used} function${used === 1 ? '' : 's'} or chain${used === 1 ? '' : 's'}.` : ''} Past results stay.`)) return;
    try {
      await api.deleteCase(c.id);
      refresh();
    } catch (e) {
      onNotice({ severity: 'error', text: errorText(e) });
    }
  };

  const enable = async () => {
    if (!target) return;
    try {
      const r = await api.linkCases(Array.from(selected), target.target_type, target.target_id);
      setEnableOpen(false);
      setSelected(new Set());
      onNotice({ severity: 'success', text: `Enabled ${r.linked + r.re_enabled} case${r.linked + r.re_enabled === 1 ? '' : 's'} on ${target.name}.` });
      refresh();
    } catch (e) {
      onNotice({ severity: 'error', text: errorText(e) });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search test cases"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Tag</InputLabel>
          <Select label="Tag" value={tag} onChange={e => { setTag(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {(data?.tags || []).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" disabled={!selected.size} onClick={() => setEnableOpen(true)}>
          Enable {selected.size || ''} on…
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing(null)}>
          New test case
        </Button>
      </Stack>
      {!data ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : !items.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography gutterBottom>{query || tag ? 'Nothing matches.' : 'No test cases yet.'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Create one here, from selected materials, or with "Save as test case" after trying a function.
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allOn}
                    indeterminate={!allOn && items.some(c => selected.has(c.id))}
                    onChange={() => setSelected(allOn ? new Set() : new Set(items.map(c => c.id)))}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Inputs</TableCell>
                <TableCell>Enabled on</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(c => {
                const links = c.links.filter(l => l.enabled);
                return (
                  <TableRow key={c.id} hover selected={selected.has(c.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={selected.has(c.id)} onChange={() => toggle(c.id)} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {c.name}
                      </Typography>
                      {c.description && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {c.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <InputChips preview={c.preview} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {links.slice(0, 3).map(l => (
                          <Tooltip key={l.link_id} title={l.has_expectation ? 'Has an expected output' : 'No expected output yet'}>
                            <Chip
                              size="small"
                              variant={l.has_expectation ? 'filled' : 'outlined'}
                              label={l.target_name || l.target_id}
                              onClick={() => navigate(targetUrl(l.target_type, l.target_id, l.function_id || undefined, account))}
                            />
                          </Tooltip>
                        ))}
                        {links.length > 3 && <Chip size="small" label={`+${links.length - 3}`} />}
                        {!links.length && (
                          <Typography variant="caption" color="text.secondary">
                            Not enabled anywhere
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                        {c.tags.slice(0, 3).map(t => <Chip key={t} size="small" label={t} />)}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton size="small" onClick={() => setEditing(c)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(c)} aria-label="Delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {data.total > pageSize && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
              <Pagination count={Math.ceil(data.total / pageSize)} page={page} onChange={(_, p) => setPage(p)} size="small" />
            </Box>
          )}
        </Paper>
      )}
      <CaseEditorDialog
        open={editing !== undefined}
        api={api}
        testCase={editing || null}
        onClose={() => setEditing(undefined)}
        onSaved={() => {
          setEditing(undefined);
          refresh();
        }}
      />
      <Dialog open={enableOpen} onClose={() => setEnableOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enable {selected.size} case{selected.size === 1 ? '' : 's'} on…</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TargetPicker api={api} value={target} onChange={setTarget} />
          </Box>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Then add what each case should return on that function's or chain's Tests tab.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnableOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={enable} disabled={!target}>
            Enable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
